<template>
  <main class="sf-page">
    <div class="sf-page-hero">
      <div class="sf-page-hero__inner" data-aos="fade-up">
        <p class="sf-page-hero__eyebrow"><span class="sf-page-hero__eyebrow-dot"></span>Restez informés</p>
        <h1 class="sf-h1">Actualités &amp; <em>Événements</em></h1>
        <p class="sf-page-hero__sub">Suivez nos dernières nouvelles, événements et réalisations</p>
      </div>
    </div>

    <div v-if="loading" class="sf-loader"><span class="sf-loader__ring"></span></div>

    <template v-else>
      <!-- Featured article -->
      <section v-if="featuredArticle" class="sf-section sf-section--cream" style="padding-bottom:0">
        <div class="sf-container">
          <span class="sf-label">À la une</span>
          <div class="bl-featured" data-aos="fade-up">
            <div class="bl-featured__media">
              <img v-if="featuredArticle.featured_image" :src="imageUrl+featuredArticle.featured_image" :alt="featuredArticle.title" loading="lazy" />
              <div v-else class="bl-featured__placeholder"><Calendar /></div>
            </div>
            <div class="bl-featured__body">
              <span class="sf-card__cat" style="position:static;border-radius:2px;margin-bottom:.75rem;display:inline-block">{{ featuredArticle.category }}</span>
              <h2 class="sf-h2" style="margin-bottom:1rem">{{ featuredArticle.title }}</h2>
              <p class="bl-featured__excerpt">{{ featuredArticle.excerpt || truncateText(featuredArticle.content,160) }}</p>
              <div class="bl-featured__meta">
                <span class="sf-chip"><Calendar />{{ formatDate(featuredArticle.published_at) }}</span>
                <span class="sf-chip"><Clock />{{ featuredArticle.reading_time_minutes||5 }} min</span>
                <span class="sf-chip"><User />{{ featuredArticle.author?.name||'Admin' }}</span>
              </div>
              <router-link :to="`/actualite/${featuredArticle.slug}`">
                <button class="sf-btn sf-btn--terra" style="margin-top:1.5rem">Lire l'article <ArrowRight class="sf-btn__icon" /></button>
              </router-link>
            </div>
          </div>
        </div>
      </section>

      <!-- Filters -->
      <section class="sf-section sf-section--cream-d" style="padding-top:2rem;padding-bottom:1rem">
        <div class="sf-container">
          <div class="bl-filter-row">
            <div class="sf-filters" style="margin-bottom:0">
              <button class="sf-pill" :class="{'sf-pill--active':selectedCategory===''}" @click="filterByCategory('')">Tous</button>
              <button v-for="cat in categories" :key="cat" class="sf-pill" :class="{'sf-pill--active':selectedCategory===cat}" @click="filterByCategory(cat)">{{ cat }}</button>
            </div>
            <select v-model="sortOrder" @change="changeSortOrder" class="sf-input" style="width:auto;padding:.5rem .85rem">
              <option value="desc">Plus récents</option>
              <option value="asc">Plus anciens</option>
            </select>
          </div>
        </div>
      </section>

      <!-- Articles grid -->
      <section class="sf-section sf-section--cream-d">
        <div class="sf-container">
          <div v-if="!regularArticles.length" class="sf-empty"><p class="sf-empty__title">Aucun article trouvé</p></div>
          <div v-else class="bl-grid">
            <article v-for="(art, i) in regularArticles" :key="art.id" class="sf-card" data-aos="fade-up" :data-aos-delay="i*80">
              <div class="sf-card__media" style="height:190px">
                <img v-if="art.featured_image" :src="imageUrl+art.featured_image" :alt="art.title" loading="lazy" />
                <div v-else class="bl-placeholder"><Calendar /></div>
                <span class="sf-card__cat">{{ art.category }}</span>
              </div>
              <div class="sf-card__body">
                <div class="bl-art-meta">
                  <span class="sf-chip"><Calendar />{{ formatDate(art.published_at) }}</span>
                  <span class="sf-chip"><Clock />{{ art.reading_time_minutes||5 }} min</span>
                  <span class="sf-chip" style="margin-left:auto"><Eye />{{ formatNumber(art.views_count) }}</span>
                </div>
                <h3 class="bl-art-title">{{ art.title }}</h3>
                <p class="bl-art-excerpt">{{ art.excerpt || truncateText(art.content,110) }}</p>
                <div v-if="JSON.parse(art.tags)?.length" class="bl-tags">
                  <span v-for="tag in JSON.parse(art.tags).slice(0,2)" :key="tag" class="sf-tag">#{{ tag }}</span>
                </div>
                <router-link :to="`/actualite/${art.slug}`" class="bl-read-link">Lire la suite <ArrowRight /></router-link>
              </div>
            </article>
          </div>

          <div v-if="pagination.last_page>1" class="sf-pagination">
            <button class="sf-page-btn" :disabled="pagination.current_page===1" @click="changePage(pagination.current_page-1)"><ChevronLeft /></button>
            <button v-for="p in displayedPages" :key="p" class="sf-page-btn" :class="{'sf-page-btn--active':pagination.current_page===p}" @click="changePage(p)">{{ p }}</button>
            <button class="sf-page-btn" :disabled="pagination.current_page===pagination.last_page" @click="changePage(pagination.current_page+1)"><ChevronRight /></button>
          </div>
        </div>
      </section>
    </template>
  </main>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import AOS from 'aos'; import 'aos/dist/aos.css'
import { Calendar, Clock, ArrowRight, User, Eye, ChevronLeft, ChevronRight } from 'lucide-vue-next'
export default {
  name:'NewsPage',
  components:{ Calendar, Clock, ArrowRight, User, Eye, ChevronLeft, ChevronRight },
  data(){return{selectedCategory:'',sortOrder:'desc',displayedPages:[]}},
  computed:{
    ...mapState({articles:s=>s.articles,featuredArticle:s=>s.featuredArticle,loading:s=>s.loading,pagination:s=>s.pagination,imageUrl:s=>s.imageUrl}),
    ...mapGetters(['regularArticles']),
    categories(){return[...new Set(this.articles.map(a=>a.category))].filter(Boolean).slice(0,5)}
  },
  watch:{'pagination.last_page':{handler(){this.updateDisplayedPages()},immediate:true},'pagination.current_page':{handler(){this.updateDisplayedPages()}}},
  created(){this.loadArticles()},
  mounted(){AOS.init({once:true,offset:70,easing:'ease-out-cubic',duration:650})},
  methods:{
    ...mapActions(['fetchPublicArticles','setFilter','setPage']),
    async loadArticles(){await this.fetchPublicArticles()},
    filterByCategory(c){this.selectedCategory=c;this.setPage(1);this.setFilter({key:'category',value:c})},
    changeSortOrder(){this.setFilter({key:'sort_order',value:this.sortOrder})},
    changePage(p){if(p>=1&&p<=this.pagination.last_page){this.setPage(p);this.fetchPublicArticles();window.scrollTo({top:0,behavior:'smooth'})}},
    updateDisplayedPages(){const c=this.pagination.current_page,l=this.pagination.last_page,d=2;let pg=[];for(let i=1;i<=l;i++){if(i===1||i===l||(i>=c-d&&i<=c+d))pg.push(i)}this.displayedPages=pg},
    formatDate(d){return d?new Date(d).toLocaleDateString('fr-FR',{day:'2-digit',month:'long',year:'numeric'}):''},
    formatNumber(n){return n>=1000?(n/1000).toFixed(1)+'k':String(n||0)},
    truncateText(t,l){return t?.length>l?t.slice(0,l)+'…':(t||'')}
  }
}
</script>
<style>@import './sf-design.css';</style>
<style scoped>
.bl-featured{display:grid;grid-template-columns:1fr 1fr;gap:0;border-radius:8px;overflow:hidden;box-shadow:var(--shadow-md);margin-top:1rem;background:var(--white)}
@media(max-width:768px){.bl-featured{grid-template-columns:1fr}}
.bl-featured__media{min-height:320px;overflow:hidden;background:var(--cream-d);position:relative}
.bl-featured__media img{width:100%;height:100%;object-fit:cover}
.bl-featured__placeholder{width:100%;height:100%;display:flex;align-items:center;justify-content:center;color:var(--sage)}
.bl-featured__placeholder :deep(svg){width:48px;height:48px}
.bl-featured__body{padding:2.5rem 2rem;display:flex;flex-direction:column}
.bl-featured__excerpt{font-size:.92rem;color:var(--ink-60);line-height:1.7;flex:1;margin-top:.5rem}
.bl-featured__meta{display:flex;flex-wrap:wrap;gap:1rem;margin-top:1.25rem}
.bl-filter-row{display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:1rem}
.bl-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(300px,1fr));gap:1.5rem}
.bl-placeholder{width:100%;height:100%;display:flex;align-items:center;justify-content:center;color:var(--sage)}
.bl-placeholder :deep(svg){width:36px;height:36px}
.bl-art-meta{display:flex;flex-wrap:wrap;gap:.5rem;margin-bottom:.75rem;align-items:center}
.bl-art-title{font-family:var(--fd);font-size:1.2rem;font-weight:600;color:var(--ink);line-height:1.25;margin-bottom:.55rem;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
.bl-art-excerpt{font-size:.83rem;color:var(--ink-60);line-height:1.55;margin-bottom:.75rem;display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden}
.bl-tags{display:flex;flex-wrap:wrap;gap:.35rem;margin-bottom:.85rem}
.bl-read-link{display:inline-flex;align-items:center;gap:.35rem;font-size:.82rem;font-weight:500;color:var(--terra);text-decoration:none;transition:gap .2s ease}
.bl-read-link:hover{gap:.6rem}
.bl-read-link :deep(svg){width:14px;height:14px}
</style>
